import { useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  FormControl,
  MenuItem,
  Select,
  Stack,
  useTheme,
  alpha,
} from "@mui/material";
import {
  CloseTwoTone,
  CloudUploadTwoTone,
  TaskAlt,
  WarningAmberOutlined,
} from "@mui/icons-material";
import LinearProgress from "@mui/material/LinearProgress";
import { toast } from "react-hot-toast";
import { UploadColoredIcon } from "src/components/logos/UploadColoredIcon";
import Papa from "papaparse";
import axios from "axios";
import { API_SERVICE_BACKEND } from "src/config";
import SampleEmails from "src/assets/samples/sample-emails.csv";
import SampleDomains from "src/assets/samples/sample-domains.csv";
import SamplePhones from "src/assets/samples/sample-phones.csv";
import SampleLinkedIns from "src/assets/samples/sample-linkedins.csv";
import { uploadToBlobStorage } from "src/utils/uploadToBlobStorage";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const emailSelections = [
  { label: "Do not import", value: "doNotImport" },
  { label: "Email", value: "email" },
];

const domainSelections = [
  { label: "Do not import", value: "doNotImport" },
  { label: "Domain", value: "domain" },
];

const phoneSelections = [
  { label: "Do not import", value: "doNotImport" },
  { label: "Phone", value: "phone" },
];

const linkedInSelections = [
  { label: "Do not import", value: "doNotImport" },
  { label: "LinkedIn", value: "linkedin" },
];

const FileUploadRow = ({
  column,
  sample,
  selections,
  onChange,
  isUploading,
  selectedValue,
  usedValues,
}) => {
  const handleChange = (event) => {
    const selection = selections.find((s) => s.value === event.target.value);
    onChange(column, selection);
  };

  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 240,
      },
    },
  };

  return (
    <TableRow>
      <TableCell>{column}</TableCell>
      <TableCell width={240}>
        <FormControl fullWidth size="small" sx={{ maxHeight: "300px" }}>
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            value={selectedValue}
            onChange={handleChange}
            variant="outlined"
            MenuProps={MenuProps}
            disabled={isUploading}
          >
            {selections.map((selection, index) => {
              // Disable option if it's already used by another dropdown (except "doNotImport" and current selection)
              const isDisabled = 
                selection.value !== "doNotImport" && 
                usedValues.includes(selection.value) && 
                selection.value !== selectedValue;
              
              return (
                <MenuItem 
                  value={selection.value} 
                  key={index}
                  disabled={isDisabled}
                >
                  {selection.label}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </TableCell>
      <TableCell>{sample}</TableCell>
    </TableRow>
  );
};

const CsvImport = ({
  setIsImportLeadsDialogOpen,
  onClose,
  selectedFile,
  setSelectedFile,
  fileInputKey,
  value,
  userEmail,
  fetchBlackListedData,
}) => {
  const theme = useTheme();
  const fileInputRef = useRef(null);

  const resetState = () => {
    setSelectedFile(null);
    setProgress(0);
    setCsvData([]);
    setSamples([]);
    setFileColumns([]);
    setColumnMappings({});
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const closeDialog = () => {
    resetState();
    if (onClose) onClose();
    else setIsImportLeadsDialogOpen(false);
  };
  const selections = value === 1 ? domainSelections : value === 2 ? phoneSelections : value === 3 ? linkedInSelections : emailSelections;
  const mappedFieldKey = value === 1 ? "domain" : value === 2 ? "phone" : value === 3 ? "linkedin" : "email";
  const [isDragActive, setIsDragActive] = useState(false);
  const [closeButtonMouseEnter, setCloseButtonMouseEnter] = useState(false);
  const [samples, setSamples] = useState([]);
  const [fileColumns, setFileColumns] = useState([]);
  const [progress, setProgress] = useState(0);
  const [csvData, setCsvData] = useState([]);
  const [columnMappings, setColumnMappings] = useState({});

  const [isUploading, setIsUploading] = useState(false);

  const handleSelectTypeChange = (column, selection) => {
    setColumnMappings({
      ...columnMappings,
      [column]: selection.value,
    });
  };

  // Get list of currently used values (excluding "doNotImport")
  const getUsedValues = () => {
    return Object.values(columnMappings).filter(value => value !== "doNotImport");
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragActive(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragActive(false);

    const file = e.dataTransfer.files[0];
    handleFileUpload(file);
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    handleFileUpload(file);
  };

  const isValidDomain = (domain) => {
    const regex =
      /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+$/;
    return domain !== "" ? regex.test(domain) : false;
  };
  const isValidEmail = (email) => {
    const regex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g;
    return email !== "" ? regex.test(email) : false;
  };
  const isValidPhone = (phone) => {
    const regex = /^[+]?[\d\s\-().]{7,20}$/;
    return phone !== "" ? regex.test(phone) : false;
  };

  const isValidLinkedIn = (url) => {
    const regex = /^(https?:\/\/)?(www\.)?linkedin\.com\/in\/[-a-zA-Z0-9_%]+\/?$/;
    return url !== "" ? regex.test(url) : false;
  };

  const isValidEntry = (val) =>
    value === 1 ? isValidDomain(val) : value === 2 ? isValidPhone(val) : value === 3 ? isValidLinkedIn(val) : isValidEmail(val);

  const handleFileUpload = (file) => {
    setProgress(0);
    if (file) {
      const allowedTypes = [
        "text/csv",  
        'application/csv',
        'application/vnd.ms-excel',
        'application/octet-stream'
      ];

      if (allowedTypes.includes(file.type)) {
        //setSelectedFile(file);

        const reader = new FileReader();

        reader.onprogress = (event) => {
          const { total, loaded } = event;
          setProgress((loaded / total) * 100);
        };

        reader.onload = (event) => {
          const contents = event.target.result;
          const lines = contents?.split("\n");
          
          // Check if it's a multi-column CSV
          const firstLineColumns = lines?.[0]?.split(",");
          const isMultiColumn = firstLineColumns?.length > 1;
          
          if (isMultiColumn) {
            // Multi-column CSV handling with column mapping
            const csv = Papa.parse(contents, { 
              header: true,
              skipEmptyLines: true 
            });
            
            if (csv?.data?.length > 10000) {
              alert(`Cannot import more than 10,000 ${value === 1 ? "domains" : value === 2 ? "phone numbers" : value === 3 ? "linkedin profiles" : "emails"}!`);
              const input = document.getElementById('import-file-input');
              input.value = null;
              setSelectedFile(null);
              return;
            }

            setSelectedFile(file);

            // Get column headers
            const columns = firstLineColumns.map(col => col.trim());
            setFileColumns(columns);

            // Initialize column mappings - try to auto-match the relevant column
            const initialMappings = {};
            columns.forEach(column => {
              const lowerCol = column.toLowerCase();
              const isMatch =
                value === 1
                  ? lowerCol === 'domain' || lowerCol === 'domains'
                  : value === 2
                    ? lowerCol === 'phone' || lowerCol === 'phones' || lowerCol === 'phone number' || lowerCol === 'phone numbers'
                    : value === 3
                      ? lowerCol === 'linkedin' || lowerCol === 'linkedin url' || lowerCol === 'linkedin profile'
                      : lowerCol === 'email' || lowerCol === 'emails';
              initialMappings[column] = isMatch ? mappedFieldKey : 'doNotImport';
            });
            setColumnMappings(initialMappings);
            
            // Set sample data (first row values)
            if (csv.data.length > 0) {
              const firstRow = csv.data[0];
              const sampleValues = columns.map(col => firstRow[col] || '');
              setSamples(sampleValues);
            }
            
            setCsvData(csv.data);
          } else {
            // Single-column CSV handling (original logic)
            if (lines?.length > 10000) {
              alert(`Cannot import more than 10,000 ${value === 1 ? "domains" : value === 2 ? "phone numbers" : value === 3 ? "linkedin profiles" : "emails"}!`);
              const input = document.getElementById('import-file-input');
              input.value = null;
              setSelectedFile(null);
              return;
            }

            // For phones/linkedin: reject if the first row is not a valid entry (e.g. a header like "Phone")
            if (value === 2 && lines.length > 0 && !isValidEntry(lines[0].trim())) {
              setSamples([]);
              setCsvData([]);
              const input = document.getElementById('import-file-input');
              if (input) input.value = null;
              setSelectedFile(null);
              return toast.error(
                "Incorrect format: do not include a header row. Add " + (value === 2 ? "phone numbers" : "LinkedIn URLs") + " only (one per row).",
                { duration: 5000, position: "top-center" }
              );
            }

            setSelectedFile(file);

            let filteredCsvData = [];

            for (const line of lines) {
              if (isValidEntry(line.trim())) {
                filteredCsvData = filteredCsvData?.concat(line.trim());
              }
            }
            if (!filteredCsvData.length) {
              setSamples([]);
              setCsvData([]);
              return toast.error(
                `${value === 1 ? "Domains" : value === 2 ? "Phone numbers" : value === 3 ? "LinkedIn profiles" : "Emails"} not found.`,
                {
                  duration: 4000,
                  position: "top-center",
                  icon: "❌",
                  iconTheme: {
                    primary: "#000",
                    secondary: "#fff",
                  },
                  ariaProps: {
                    role: "status",
                    "aria-live": "polite",
                  },
                }
              );
            }
            setSamples(filteredCsvData?.slice(0, 5));
            setCsvData(filteredCsvData);
          }
        };

        reader.readAsText(file);
      } else {
        alert("Only .csv files are allowed.");
      }
    }
  };

  const handleDeleteFile = () => {
    fileInputRef.current.value = "";
    setSelectedFile(null);
    setCloseButtonMouseEnter(false);
    setProgress(0);
    setFileColumns([]);
    setCsvData([]);
    setColumnMappings({});
  };

  const handleSubmit = () => {
    handleFileUploadAll();
  };







  function generateUniqueId() {
    const now = new Date();
    const year = now.getFullYear().toString().slice(-2);
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    const seconds = now.getSeconds().toString().padStart(2, '0');
    const milliseconds = now.getMilliseconds().toString().padStart(3, '0');
    
    const uniqueId = `${year}${month}${day}${hours}${minutes}${seconds}${milliseconds}`;
    
    return uniqueId;
  }

  const handleFileUploadAll = async () => {
    if (!selectedFile) {
      return;
    }

    try {
      setIsUploading(true);

      // ── Phone numbers / LinkedIn: upload CSV to blob storage then call addNumberToExclusionList ──
      if (value === 2 || value === 3) {
        let phoneNumbers = [];

        if (fileColumns.length > 0) {
          const mappedColumn = Object.keys(columnMappings).find(
            key => columnMappings[key] === mappedFieldKey
          );
          if (!mappedColumn) {
            toast.error(`Please map at least one column to '${value === 2 ? "Phone" : "LinkedIn"}'`);
            setIsUploading(false);
            return;
          }
          phoneNumbers = csvData
            .map(row => row[mappedColumn])
            .filter(val => val && isValidEntry(val?.trim()))
            .map(val => val.trim());
        } else {
          // Single-column CSV — csvData is already the filtered array
          phoneNumbers = csvData;
        }

        if (phoneNumbers.length === 0) {
          toast.error(`No valid ${value === 2 ? "phone numbers" : "LinkedIn profiles"} found in the file`);
          setIsUploading(false);
          return;
        }

        // Upload CSV to blob storage
        const csvContent = phoneNumbers.join('\n');
        const phoneBlob = new Blob([csvContent], { type: 'text/csv' });
        const phoneFile = new File([phoneBlob], selectedFile.name, { type: 'text/csv' });
        const now = new Date().toISOString();
        const newName = `${now}_${generateUniqueId()}-${selectedFile.name}`;
        const uploadType = value === 2 ? "phones" : "linkedins";
        const fileUrl = await uploadToBlobStorage(phoneFile, newName, uploadType);

        // Save file record in uploadedExclusionList (same schema as emails/domains)
        await axios.put(
          `${API_SERVICE_BACKEND}/exclusionlist/updateExclusionList`,
          {
            clientEmail: userEmail,
            uploadedExclusionList: [{ url: fileUrl, operation: value === 2 ? "addPhones" : "addLinkedIns" }],
          }
        );

        setIsUploading(false);
        fetchBlackListedData(value === 2 ? "phones" : "linkedins");
        toast.success("Successfully uploaded!");
        closeDialog();
        return;
      }

      // ── Emails / Domains: existing blob-upload flow ──
      let fileToUpload = selectedFile;

      if (fileColumns.length > 0) {
        const mappedColumn = Object.keys(columnMappings).find(
          key => columnMappings[key] === mappedFieldKey
        );
        if (!mappedColumn) {
          toast.error(`Please map at least one column to '${value === 1 ? "Domain" : "Email"}'`);
          setIsUploading(false);
          return;
        }

        const values = csvData
          .map(row => row[mappedColumn])
          .filter(val => val && isValidEntry(val?.trim()))
          .map(val => val.trim());

        if (values.length === 0) {
          toast.error(`No valid ${value === 1 ? "domains" : "emails"} found in the selected column`);
          setIsUploading(false);
          return;
        }

        const csvContent = values.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        fileToUpload = new File([blob], selectedFile.name, { type: 'text/csv' });
      }

      const now = new Date().toISOString();
      const newName = `${now}_${generateUniqueId()}-${selectedFile.name}`;
      const fileUrl = await uploadToBlobStorage(fileToUpload, newName, value === 1 ? "domains" : "emails");

      const body =
        value === 0
          ? {
              clientEmail: userEmail,
              uploadedExclusionList: [{ url: fileUrl, operation: "addEmails" }],
            }
          : {
              clientEmail: userEmail,
              uploadedExclusionList: [{ url: fileUrl, operation: "addDomains" }],
            };

      await axios.put(
        `${API_SERVICE_BACKEND}/exclusionlist/updateExclusionList`,
        body
      );
      setIsUploading(false);
      fetchBlackListedData(value === 1 ? "domains" : "emails");
      toast.success("Successfully uploaded!");
      closeDialog();
    } catch (error) {
      setIsUploading(false);
      console.error("Error during file upload:", error);
      toast.error("Something went wrong!");
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        width: "100%",
      }}
    >
      <Stack
        sx={{
          borderRadius: 1,
          border: "1px solid red",
          width: "auto",
          p: 1,
        }}
        rowGap={1}
      >
        <Typography
          sx={{
            fontSize: "14px",
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <WarningAmberOutlined sx={{ color: "red", fontSize: "18px" }} />
          Please ensure that the uploaded file is in the correct format.
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography sx={{ fontSize: "14px" }}>
            Refer to the sample format provided here:
          </Typography>
          <a
            href={value === 1 ? SampleDomains : value === 2 ? SamplePhones : value === 3 ? SampleLinkedIns : SampleEmails}
            download
            style={{
              textDecoration: "none",
              fontSize: "14px",
              fontWeight: 500,
            }}
          >
            Sample {value === 1 ? "Domains" : value === 2 ? "Phones" : value === 3 ? "LinkedIn Profiles" : "Emails"}
          </a>
        </Box>
      </Stack>

      <Paper
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        sx={{
          width: "100%",
          height: "260px",
          borderRadius: "12px",
          border: `1px dashed ${theme.palette.primary.main}`,
          mt: 2,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          cursor: "pointer",
          position: "relative",
          boxShadow: "0px 12px 15px 0px #4B71970D",
          backgroundColor: alpha(theme.palette.primary.main, 0.05),
        }}
        className="glass-wrapper-50"
        onClick={() => {
          !closeButtonMouseEnter && fileInputRef.current.click();
        }}
      >
        <input
          key={fileInputKey}
          type="file"
          // multiple
          id="import-file-input"
          style={{ display: "none" }}
          onChange={handleFileInputChange}
          ref={fileInputRef}
          accept=".csv"
          disabled={isUploading}
        />

        {selectedFile ? (
          <>
            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: 700,
                lineHeight: "25px",
                color: theme.palette.primary.main,
              }}
            >
              Size: {(selectedFile.size / 1000).toFixed(2)} KB
            </Typography>
          </>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mb: 2,
            }}
          >
            <UploadColoredIcon fillColor={alpha(theme.palette.primary.main, 0.1)} strokeColor={theme.palette.primary.main} />
          </Box>
        )}

        <Typography
          sx={{
            fontSize: "20px",
            fontWeight: 700,
            lineHeight: "25px",
            color: theme.palette.primary.main,
          }}
        >
          {selectedFile
            ? selectedFile.name
            : isDragActive
            ? "Drop the files here ..."
            : "Drag files to this area or click to pick files."}
        </Typography>
        {selectedFile && (
          <IconButton
            onMouseEnter={() => {
              setCloseButtonMouseEnter(true);
            }}
            onMouseLeave={() => {
              setCloseButtonMouseEnter(false);
            }}
            sx={{ position: "absolute", right: 0, top: 0, m: 1 }}
            onClick={handleDeleteFile}
            disabled={isUploading}
          >
            <CloseTwoTone sx={{ color: "black" }} />
          </IconButton>
        )}
        {selectedFile && (
          <Box
            sx={{ width: "80%", mt: 2, display: progress === 100 && "none" }}
          >
            <LinearProgress variant="determinate" value={progress} />
          </Box>
        )}
      </Paper>
      {selectedFile && progress === 100 && (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              mt: 2,
            }}
          >
            <TaskAlt sx={{ color: theme.palette.primary.main, mr: 1 }} />
            <Typography sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
              Files Processed
            </Typography>
          </Box>
          {csvData?.length > 0 && (
            <>
              {fileColumns.length > 0 ? (
                // Multi-column CSV - show mapping table
                <>
                  <TableContainer sx={{ mt: 2 }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell width={360}>CSV Headers</TableCell>
                          <TableCell width={360}>Map to</TableCell>
                          <TableCell>Samples</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {fileColumns.map((column, index) => (
                          <FileUploadRow
                            key={index}
                            column={column}
                            sample={samples[index]}
                            selections={selections}
                            onChange={handleSelectTypeChange}
                            isUploading={isUploading}
                            selectedValue={columnMappings[column] || 'doNotImport'}
                            usedValues={getUsedValues()}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              ) : (
                // Single-column CSV - show simple preview
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    gap: 1,
                    m: 2,
                  }}
                >
                  <Typography
                    sx={{ fontSize: "16px", fontWeight: 500, minWidth: "150px" }}
                  >
                    Sample {value === 1 ? "Domains" : value === 2 ? "Phones" : value === 3 ? "LinkedIn Profiles" : "Emails"}
                  </Typography>
                  <Typography sx={{ fontSize: "14px" }}>
                    {samples?.join(", ")}...
                  </Typography>
                </Box>
              )}
            </>
          )}

          <Button
            variant="contained"
            sx={{
              fontSize: "16px",
              px: 3,
              py: 1.5,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mt: 1,
            }}
            onClick={handleSubmit}
            disabled={!csvData.length}
          >
            {isUploading ? (
              <CircularProgress size={20} sx={{ color: "white", mr: 1 }} />
            ) : (
              <CloudUploadTwoTone sx={{ mr: 1 }} />
            )}
            Submit
          </Button>
        </>
      )}
    </Box>
  );
};

export default CsvImport;
